import type { OutlineItem } from "@/lib/blocks";
import { cn } from "@/lib/utils";

/**
 * The "On this page" rail and the page frame that reserves room for it on wide
 * screens. The note view and the editors share these so the content column sits
 * at the same x whether you are reading or editing: switching must not jump.
 */
export function hasRail(outline: OutlineItem[] | undefined): boolean {
  return (outline?.length ?? 0) >= 2;
}

export function frameClass(rail: boolean): string {
  return cn("mx-auto", rail ? "max-w-5xl xl:grid xl:grid-cols-[minmax(0,1fr)_13rem] xl:gap-10" : "max-w-3xl");
}

export function columnClass(rail: boolean): string {
  return cn("min-w-0", rail && "xl:max-w-3xl");
}

export function OutlineRail({
  outline,
  activeHash,
  links = true,
}: {
  outline: OutlineItem[];
  activeHash?: string;
  /** False in the editor: headings are listed but there is nothing to scroll to. */
  links?: boolean;
}) {
  return (
    <aside className="hidden xl:block">
      <div className="sticky top-8">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">On this page</div>
        <ul className="border-l text-sm">
          {outline.map((o) => {
            const style = { paddingLeft: 12 + Math.max(0, o.depth - 1) * 12 };
            const base = "-ml-px block truncate border-l border-transparent py-1 pr-2 text-muted-foreground";
            return (
              <li key={o.id}>
                {links ? (
                  <a href={`#${o.id}`} style={style} className={cn(base, "transition-colors hover:border-foreground hover:text-foreground", activeHash === `#${o.id}` && "border-foreground text-foreground")}>
                    {o.text}
                  </a>
                ) : (
                  <span style={style} className={base}>
                    {o.text}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
}
